import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tooltip
} from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectEndorsmentFormData,
  updateEndorsementFormData
} from 'src/features/rfqDataSlice';
import {
  customComponentMap,
  customComponentSettings
} from './assetListCustomComponents.js';
import moment from 'moment';
import FireFloaterEditable from './FireFloaterEditable';

function AssetListItemHorizontalEditable({ sumInsuredData, groupDetails, productId = '', saveRow = () => { } }) {
  const dispatch = useDispatch();
  const endorsementFormData = useSelector(selectEndorsmentFormData);
  const [vehicleModelData, setVehicleModelData] = React.useState([]);
  const [vehicleVariantData, setVehicleVariantData] = React.useState([]);

  const sectionId = sumInsuredData?.id;
  const groupId = groupDetails?.id;

  const tableListDatas = endorsementFormData?.sumInsuredSectionData?.[sectionId]?.groups?.[groupId]?.sumInsuredEntry || [];
  const hasItemForUpdate = tableListDatas?.filter((item) => item.actionType == 'update');
  const renderValue = (attr, value) => {
    if (!value) return 'N/A';
    if ([
      'dateOfManufacture',
      'purchaseDate',
      'installationDate',
      'expectedDateOfCommencementOfErection'
    ].includes(attr)) {
      return moment(value).format('DD-MM-YYYY');
    }
    return Array.isArray(value) ? value.join(', ') : value;
  };
  const columns = sumInsuredData?.sumInsuredHeaderSorted || [];
  const deletedRows = tableListDatas.filter((r) => r.actionType === 'delete');
  const createdRows = tableListDatas.filter((r) => r.actionType === 'create');


  const [cellEditMap, setCellEditMap] = React.useState({});
  const [cellInputMap, setCellInputMap] = React.useState({});
  const [cellStaticMap, setCellStaticMap] = React.useState({});

  const handleChange = (attr, rowIndex, value) => {
    const actualValue = value?.target?.value ?? value;
    const updatedEntryList = [...tableListDatas];
    updatedEntryList[rowIndex] = {
      ...updatedEntryList[rowIndex],
      [attr]: actualValue,
      savedStatus: false,
      actionType: (updatedEntryList[rowIndex]?.actionType == null || updatedEntryList[rowIndex]?.actionType == '') ? 'update' : updatedEntryList[rowIndex]?.actionType
    };

    const updatedGroup = {
      ...endorsementFormData.sumInsuredSectionData[sectionId].groups[groupId],
      sumInsuredEntry: updatedEntryList
    };

    const updatedSection = {
      ...endorsementFormData.sumInsuredSectionData[sectionId],
      groups: {
        ...endorsementFormData.sumInsuredSectionData[sectionId].groups,
        [groupId]: updatedGroup
      }
    };

    dispatch(
      updateEndorsementFormData({
        sumInsuredSectionData: {
          ...endorsementFormData.sumInsuredSectionData,
          [sectionId]: updatedSection
        }
      })
    );
  };

  const formatValue = (val: unknown): string => {
    if (!val) return 'N/A';

    // If it's already a Date
    if (val instanceof Date) {
      const day = String(val.getDate()).padStart(2, '0');
      const month = String(val.getMonth() + 1).padStart(2, '0');
      const year = val.getFullYear();
      return `${day}-${month}-${year}`;
    }

    // If it's an ISO string
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
      const date = new Date(val); // ✅ safe, val is string
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
    }

    // If it's an array
    if (Array.isArray(val)) {
      return val.join(', ');
    }

    // Fallback
    return String(val);
  };

  const renderEditableCell = (attr, rowIndex, head, cellKey) => {
    const inputValue = cellInputMap[cellKey] || '';

    if (
      ['dateOfManufacture', 'purchaseDate', 'installationDate', 'expectedDateOfCommencementOfErection'].includes(attr)
    ) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={['year', 'month', 'day']}
            inputFormat="dd-MM-yyyy"
            value={inputValue ? moment(inputValue, "DD-MM-YYYY").toDate() : null}
            onChange={(val) => {
              const formattedDate = val ? moment(val).format("DD-MM-YYYY") : null;

              setCellInputMap({
                ...cellInputMap,
                [cellKey]: formattedDate
              });

              handleChange(attr, rowIndex, formattedDate);
            }}
            maxDate={new Date()}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                style={{ minWidth: "180px" }}
              />
            )}
          />
        </LocalizationProvider>
      );
    }

    if (['wetRisk', 'hillyAreas', 'undergroundWork'].includes(attr)) {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{head.title || attr}</InputLabel>
          <Select
            label={head.title || attr}
            value={inputValue || ''}
            onChange={(e) => {
              const newVal = e.target.value;
              setCellInputMap({
                ...cellInputMap,
                [cellKey]: newVal
              });
              handleChange(attr, rowIndex, e);
            }}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    const CustomComponent = customComponentMap[attr];
    const CustomSetting = customComponentSettings[attr];
    if (CustomComponent && CustomSetting) {
      const commonProps = {
        tableListDatas,
        index: rowIndex,
        productId,
        saveRow,
        label: head.title || head.sumInsuredHeader.title || attr,
        apiUrl: CustomSetting,
        handleChange
      };
      const extraProps =
        attr === 'vehicleModel' ? { vehicleModelData, setVehicleModelData, setVehicleVariantData } :
          attr === 'vehicleVariant' ? { vehicleModelData, vehicleVariantData, setVehicleVariantData } :
            attr === 'vehicleMake' ? { setVehicleModelData } :
              {};

      return <CustomComponent {...commonProps} {...extraProps} />;

    }

    return (
      <TextField
        size="small"
        fullWidth
        value={inputValue}
        onChange={(e) => {
          const newVal = e.target.value;
          setCellInputMap({
            ...cellInputMap,
            [cellKey]: newVal
          });
        }}
        onBlur={() => {
          handleChange(attr, rowIndex, inputValue);
        }}
      />
    );
  };



  return (
    <>
      {sumInsuredData?.isFireFloaterEnabled && (
        <FireFloaterEditable
          sectionId={sectionId} />
      )}
      <TableContainer sx={{ mt: 1 }} component={Paper}>


        <Table size="small">
          {hasItemForUpdate?.length > 0 && <TableHead>
            <TableRow>
              <TableCell>SR No</TableCell>
              {sumInsuredData?.sumInsuredHeaderSorted?.map((head) => {
                const attr = head.sumInsuredHeader.attribute;
                return (
                  <TableCell key={attr}>
                    {head.title || head.sumInsuredHeader.title}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>}


          <TableBody>
            {tableListDatas?.map((row, rowIndex) => {
              const isHidden =
                row.actionType === "delete" || row.actionType === "create";

              if (isHidden) return null;
              return (
                <TableRow key={rowIndex}>
                  <TableCell>{rowIndex + 1}</TableCell>

                  {sumInsuredData.sumInsuredHeaderSorted.map(head => {
                    const attr = head.sumInsuredHeader.attribute;
                    const value = row[attr];
                    const cellKey = `${rowIndex}_${attr}`;
                    const isCellEditable = cellEditMap[cellKey] || false;
                    const isEditableAllowed =
                      endorsementFormData?.type === 'Both' ||
                      (endorsementFormData?.type === 'Financial' &&
                        head?.endorsementSubTypes?.financial?.includes(endorsementFormData?.subType)) ||
                      (endorsementFormData?.type === 'Non-Financial' &&
                        head?.endorsementSubTypes?.nonFinancial?.includes(endorsementFormData?.subType));

                    return (
                      <TableCell key={cellKey}>
                        {!isEditableAllowed ? (
                          <Tooltip title="Editing disabled due to subtype restriction">
                            <span>
                              <Checkbox
                                checked={isCellEditable}
                                disabled
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Checkbox
                            checked={isCellEditable}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setCellEditMap({
                                ...cellEditMap,
                                [cellKey]: checked
                              });
                              if (checked) {
                                setCellStaticMap({
                                  ...cellStaticMap,
                                  [cellKey]: value
                                });
                                setCellInputMap({
                                  ...cellInputMap,
                                  [cellKey]: value || ''
                                });
                              }
                            }}
                          />
                        )}


                        <Typography display="inline" sx={{ mr: 1 }}>
                          {cellStaticMap[cellKey] !== undefined
                            ? formatValue(cellStaticMap[cellKey])
                            : formatValue(value)}
                        </Typography>

                        {isCellEditable && (
                          <div style={{ marginTop: 4 }}>
                            {renderEditableCell(attr, rowIndex, head, cellKey)}
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>


      {createdRows.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>Newly Added Entries</Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SR NO</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.sumInsuredHeader.attribute}>
                      {col.title || col.sumInsuredHeader.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {createdRows.map((row, index) => (
                  <TableRow key={row.id} style={{ backgroundColor: '#eaffea' }}>
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((col) => {
                      const attr = col.sumInsuredHeader.attribute;
                      return <TableCell key={attr}>{renderValue(attr, row[attr])}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {deletedRows.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 1 }}>Deleted Entries</Typography>
          <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>SR NO</TableCell>
                  {columns.map((col) => (
                    <TableCell key={col.sumInsuredHeader.attribute}>
                      {col.title || col.sumInsuredHeader.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {deletedRows.map((row, index) => (
                  <TableRow key={row.id} style={{ backgroundColor: '#fdeaea' }}>
                    <TableCell>{index + 1}</TableCell>
                    {columns.map((col) => {
                      const attr = col.sumInsuredHeader.attribute;
                      return <TableCell key={attr}>{renderValue(attr, row[attr])}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

export default AssetListItemHorizontalEditable;
